import React, { useState, useRef } from "react";
import axios from "axios";
import { getDatabase, ref, push } from "firebase/database";
import { FiRefreshCw, FiSend, FiXCircle, FiUploadCloud } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";
import s3Uploader from "./utils/s3Upload";

const AdminNewsGenerator = () => {
  const [title, setTitle] = useState("");
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [serperImages, setSerperImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [editableContent, setEditableContent] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [editableTitle, setEditableTitle] = useState("");
  const [editableDescription, setEditableDescription] = useState("");
  const [serperApiError, setSerperApiError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const generatedContentRef = useRef(null);

  // Hàm kiểm tra ảnh có thể truy cập được hay không
  const isImageAccessible = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  // Thêm hàm xử lý upload ảnh lên S3
  const uploadToS3 = async (file) => {
    try {
      const taskId = s3Uploader.generateTaskId();
      const uploadResult = await s3Uploader.uploadFile(file, taskId, "news");

      if (uploadResult.success) {
        return uploadResult.s3_url;
      } else {
        throw new Error(uploadResult.error || "S3 upload failed");
      }
    } catch (error) {
      console.error("Failed to upload image to S3:", error);
      throw error;
    }
  };

  // Cập nhật hàm handleImageUpload
  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      const remainingSlots = 4 - selectedImages.length;
      if (remainingSlots <= 0) {
        toast.warning("Bạn chỉ có thể tải lên tối đa 4 ảnh");
        return;
      }

      setIsUploading(true);

      try {
        const filesToProcess = Array.from(files).slice(0, remainingSlots);
        for (const file of filesToProcess) {
          const s3Url = await uploadToS3(file);
          setSelectedImages((prevImages) => [...prevImages, s3Url]);
          setSelectedImage(s3Url); // Tự động chọn ảnh mới upload
        }
      } catch (error) {
        toast.error("Có lỗi xảy ra khi tải ảnh lên!", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Cập nhật hàm handleDrop
  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const remainingSlots = 4 - selectedImages.length;
      if (remainingSlots <= 0) {
        toast.warning("Bạn chỉ có thể tải lên tối đa 4 ảnh");
        return;
      }

      const filesToAdd = Array.from(files).slice(0, remainingSlots);
      setIsUploading(true);

      try {
        for (const file of filesToAdd) {
          const s3Url = await uploadToS3(file);
          setSelectedImages((prevImages) => [...prevImages, s3Url]);
          setSelectedImage(s3Url); // Tự động chọn ảnh mới upload
        }
      } catch (error) {
        toast.error("Có lỗi xảy ra khi tải ảnh lên!", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Tạo bài viết với Gemini API
  const generatePost = async () => {
    if (!title.trim()) {
      toast.error("Vui lòng nhập tiêu đề bài viết!", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    setIsGenerating(true);
    setSelectedImage(null);
    setSerperImages([]);
    setSerperApiError(false);

    const promptRequest = `
Tạo một bài viết tin tức SEO-optimized với tiêu đề: ${title}.

Yêu cầu chi tiết về nội dung:
1. Độ dài: 1000-1500 từ để đảm bảo độ sâu và giá trị cho người đọc
2. Cấu trúc bài viết:
   - Đoạn mở đầu (100-150 từ) với từ khóa chính
   - 3-4 tiêu đề phụ (H2) với các từ khóa phụ liên quan
   - Các đoạn văn ngắn (2-3 câu) để dễ đọc
   - Kết luận với call-to-action

3. Tối ưu SEO:
   - Sử dụng từ khóa chính tự nhiên trong đoạn đầu tiên
   - Phân bố từ khóa phụ đều trong bài
   - Tạo các câu hỏi người dùng thường tìm kiếm và trả lời
   - Sử dụng bullet points và danh sách để tăng khả năng đọc
   - Thêm các số liệu thống kê và dữ liệu thực tế khi có thể

4. Định dạng HTML:
   - Sử dụng <h1> cho tiêu đề chính
   - <h2> cho các tiêu đề phụ
   - <h3> cho các tiêu đề nhỏ
   - <p> cho các đoạn văn
   - <ul> và <li> cho danh sách
   - <strong> cho từ khóa quan trọng
   - <em> cho thông tin cần nhấn mạnh
   - <blockquote> cho trích dẫn quan trọng

5. Yêu cầu về chất lượng:
   - Nội dung độc đáo, không copy
   - Thông tin chính xác và cập nhật
   - Giọng văn chuyên nghiệp, dễ hiểu
   - Tối ưu cho mobile-first
   - Thêm các ví dụ thực tế và case study khi phù hợp
   - Không được chứa các thông tin nhạy cảm, không phù hợp với đối tượng người dùng
   - Không được chứa các thông tin không rõ ràng, không chính xác
   - Bài viết hoàn chỉnh

6. Tối ưu cho người dùng:
   - Đoạn văn ngắn (2-3 câu)
   - Sử dụng ngôn ngữ đơn giản
   - Thêm các ví dụ minh họa
   - Tạo các điểm nhấn bằng bullet points

Hãy trả về kết quả theo định dạng JSON với cấu trúc:
{
  "content": "Nội dung bài viết hoàn chỉnh với định dạng HTML",
  "description": "Mô tả ngắn gọn về bài viết (150-160 ký tự) với từ khóa chính"
}
    `;

    try {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`;

      const data = {
        contents: [
          {
            parts: [
              {
                text: promptRequest,
              },
            ],
          },
        ],
      };

      const headers = {
        "Content-Type": "application/json",
      };

      const response = await axios.post(url, data, { headers });
      const responseData = response.data;

      let jsonData = responseData.candidates[0].content.parts[0].text;
      jsonData = jsonData.replace("```json", "").replace("```", "");
      const rawData = JSON.parse(jsonData);

      // Trích xuất nội dung từ thẻ h1
      const h1Match = rawData.content.match(/<h1>(.*?)<\/h1>/);
      const extractedTitle = h1Match ? h1Match[1] : title;

      setEditableTitle(extractedTitle);
      setEditableDescription(rawData?.description || "");

      // Sau khi có nội dung bài viết, tìm hình ảnh với Serper API
      const serperData = {
        q: title,
        page: 1,
      };

      try {
        const serperResponse = await axios.post(
          "https://google.serper.dev/images",
          serperData,
          {
            headers: {
              "Content-Type": "application/json",
              "X-API-KEY": process.env.REACT_APP_SERPER_API_KEY,
            },
          }
        );

        // Lọc các hình ảnh có định dạng phù hợp
        const filteredImages = serperResponse.data?.images?.filter(
          (img) =>
            img.imageUrl.includes(".jpg") ||
            img.imageUrl.includes(".png") ||
            img.imageUrl.includes(".jpeg")
        );

        // Kiểm tra khả năng truy cập của từng hình ảnh
        const accessibleImages = [];
        if (filteredImages && filteredImages.length > 0) {
          for (const img of filteredImages.slice(0, 15)) {
            try {
              const isAccessible = await isImageAccessible(img.imageUrl);
              if (isAccessible) {
                accessibleImages.push(img);
                if (accessibleImages.length >= 10) break; // Giới hạn số lượng ảnh
              }
            } catch (error) {
              console.error("Lỗi kiểm tra hình ảnh:", error);
            }
          }
        }

        setSerperImages(accessibleImages || []);

        // Tìm ảnh đầu tiên có thể truy cập được làm ảnh mặc định
        let firstAccessibleImage = null;
        if (accessibleImages.length > 0) {
          firstAccessibleImage = accessibleImages[0];
          setSelectedImage(firstAccessibleImage.imageUrl);
        }
      } catch (error) {
        console.error("Lỗi khi lấy ảnh từ Serper API:", error);
        setSerperApiError(true);
        toast.error(
          "Không thể tải hình ảnh từ API. Vui lòng tải ảnh của bạn lên.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }

      setGeneratedContent({
        title: extractedTitle,
        content: rawData?.content,
        description: rawData?.description,
      });
      setEditableContent(rawData?.content || "");

      // Cuộn xuống kết quả sau khi tạo bài viết
      setTimeout(() => {
        if (generatedContentRef.current) {
          generatedContentRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    } catch (error) {
      console.error("Lỗi khi tạo bài viết:", error);
      toast.error("Có lỗi xảy ra khi tạo bài viết. Vui lòng thử lại sau!", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Đăng bài viết lên Firebase Realtime Database
  const publishPost = async () => {
    if (!generatedContent) {
      toast.error("Vui lòng tạo bài viết trước!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!selectedImage) {
      toast.error("Vui lòng chọn ảnh đại diện cho bài viết!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsPublishing(true);

    try {
      const database = getDatabase();
      const newsRef = ref(database, "news");

      const newPost = {
        title: editableTitle,
        content: editableContent,
        description: editableDescription,
        imageUrl: selectedImage,
        createdAt: new Date().toISOString(),
      };

      await push(newsRef, newPost);

      toast.success("Đăng bài viết thành công!", {
        position: "top-right",
        autoClose: 3000,
      });

      // Reset form sau khi đăng bài thành công
      setTitle("");
      setGeneratedContent(null);
      setSelectedImage(null);
      setSerperImages([]);
      setEditableTitle("");
      setEditableDescription("");
      setEditableContent("");
    } catch (error) {
      console.error("Lỗi khi đăng bài viết:", error);
      toast.error("Có lỗi xảy ra khi đăng bài viết. Vui lòng thử lại sau!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // Mở xem trước ảnh
  const openImagePreview = (imageUrl) => {
    const imagesList = serperImages;
    const index = imagesList.findIndex((img) => img.imageUrl === imageUrl);
    setCurrentImageIndex(index);
    setPreviewImage(imageUrl);
  };

  // Đóng xem trước ảnh
  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  // Xem ảnh trước đó
  const handlePrevImage = (e) => {
    e.stopPropagation();
    const imagesList = serperImages;
    const newIndex =
      (currentImageIndex - 1 + imagesList.length) % imagesList.length;
    setCurrentImageIndex(newIndex);
    setPreviewImage(imagesList[newIndex].imageUrl);
  };

  // Xem ảnh tiếp theo
  const handleNextImage = (e) => {
    e.stopPropagation();
    const imagesList = serperImages;
    const newIndex = (currentImageIndex + 1) % imagesList.length;
    setCurrentImageIndex(newIndex);
    setPreviewImage(imagesList[newIndex].imageUrl);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-4 md:px-8 pt-6 pb-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          TẠO BÀI VIẾT TIN TỨC MỚI
        </h1>

        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8">
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Tiêu đề bài viết
            </label>
            <div className="relative">
              <input
                type="text"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Nhập tiêu đề bài viết..."
              />
              {title && (
                <button
                  onClick={() => setTitle("")}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                  title="Xóa nội dung"
                >
                  <FiXCircle size={20} />
                </button>
              )}
            </div>
          </div>

          <button
            onClick={generatePost}
            disabled={isGenerating}
            className="flex items-center justify-center w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <FiRefreshCw
              className={`mr-2 ${isGenerating ? "animate-spin" : ""}`}
            />
            {isGenerating ? "Đang tạo bài viết..." : "Tạo bài viết"}
          </button>
        </div>

        {generatedContent && (
          <div
            ref={generatedContentRef}
            className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8"
          >
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Tiêu đề bài viết:</h3>
              <input
                type="text"
                value={editableTitle}
                onChange={(e) => setEditableTitle(e.target.value)}
                className="w-full p-3 bg-gray-50 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Tiêu đề bài viết..."
              />
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Mô tả ngắn:</h3>
              <textarea
                value={editableDescription}
                onChange={(e) => setEditableDescription(e.target.value)}
                className="w-full p-3 bg-gray-50 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Mô tả ngắn..."
                rows="3"
              />
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium mb-2">Nội dung bài viết:</h3>
              <div className="space-y-4">
                <textarea
                  value={editableContent}
                  onChange={(e) => {
                    setEditableContent(e.target.value);
                    setGeneratedContent((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }));
                  }}
                  className="w-full h-[500px] p-3 bg-gray-50 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="Nội dung bài viết..."
                />
                <div className="p-3 bg-gray-50 rounded-md prose prose-lg max-w-none">
                  <div
                    dangerouslySetInnerHTML={{ __html: editableContent }}
                  ></div>
                </div>
              </div>
            </div>

            {serperImages.length > 0 && selectedImages.length === 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-medium mb-2">
                  Chọn ảnh đại diện cho bài viết:
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {serperImages.map((img, index) => (
                    <div key={index} className="relative">
                      <img
                        src={img.imageUrl}
                        onClick={() => setSelectedImage(img.imageUrl)}
                        className={`w-full h-40 object-contain bg-gray-100 cursor-pointer ${
                          selectedImage === img.imageUrl
                            ? "border-4 border-blue-500"
                            : ""
                        }`}
                        alt="Ảnh đại diện"
                        onError={(e) => {
                          e.target.parentNode.style.display = "none";
                        }}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openImagePreview(img.imageUrl);
                        }}
                        className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path
                            fillRule="evenodd"
                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(selectedImages.length > 0 || serperApiError) && (
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-md font-medium">
                    Ảnh đã tải lên ({selectedImages.length}/4)
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedImages([]);
                      setSelectedImage(null);
                    }}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Xóa tất cả
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {selectedImages.map((imageUrl, index) => (
                    <div key={index} className="relative">
                      <img
                        src={imageUrl}
                        onClick={() => setSelectedImage(imageUrl)}
                        className={`w-full h-40 object-contain bg-gray-100 rounded-md cursor-pointer ${
                          selectedImage === imageUrl
                            ? "border-4 border-blue-500"
                            : ""
                        }`}
                        alt={`Ảnh đã tải lên ${index + 1}`}
                      />
                      <button
                        onClick={() => {
                          const newImages = [...selectedImages];
                          newImages.splice(index, 1);
                          setSelectedImages(newImages);
                          if (selectedImage === imageUrl) {
                            setSelectedImage(null);
                          }
                        }}
                        className="absolute top-2 right-2 bg-gray-800 bg-opacity-60 hover:bg-opacity-80 text-white p-1 rounded-full"
                        title="Xóa ảnh"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div
              className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-md p-4 cursor-pointer hover:border-blue-500 transition-colors mt-6"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleUploadClick}
            >
              <FiUploadCloud className="text-gray-400 text-4xl mb-2" />
              <p className="text-gray-600 text-center">
                {selectedImages.length > 0
                  ? `Kéo & thả ảnh vào đây, hoặc nhấn để tải thêm ảnh (${selectedImages.length}/4)`
                  : "Kéo & thả ảnh vào đây, hoặc nhấn để tải ảnh từ thiết bị (tối đa 4 ảnh)"}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <button
              onClick={publishPost}
              className="flex items-center justify-center w-full gap-2 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 mt-6"
              disabled={isPublishing}
            >
              {isPublishing ? (
                <ClipLoader size={20} color={"#fff"} />
              ) : (
                <FiSend />
              )}
              {isPublishing ? "Đang đăng bài..." : "Đăng bài viết"}
            </button>
          </div>
        )}

        {previewImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={closeImagePreview}
          >
            <div className="relative max-w-4xl w-full h-full p-4 flex items-center justify-center">
              {/* Previous button */}
              <button
                onClick={handlePrevImage}
                className="absolute left-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Close button */}
              <button
                onClick={closeImagePreview}
                className="absolute top-6 right-6 text-white hover:text-gray-300"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Image */}
              <img
                src={previewImage}
                alt="Preview"
                className="max-h-full max-w-full object-contain"
                onClick={(e) => e.stopPropagation()}
                onError={(e) => {
                  toast.error(
                    "Không thể tải ảnh xem trước. Hình ảnh có thể không khả dụng.",
                    {
                      position: "top-right",
                      autoClose: 3000,
                    }
                  );
                  closeImagePreview();
                }}
              />

              {/* Next button */}
              <button
                onClick={handleNextImage}
                className="absolute right-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {/* Image counter */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full">
                {currentImageIndex + 1} / {serperImages.length}
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        pauseOnHover={true}
        draggable={true}
      />
    </div>
  );
};

export default AdminNewsGenerator;
